import {Component, EventEmitter, Input, Output} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {
  ItemNumericProperty,
  NUMERIC_PROPERTIES,
  PropertyCharacteristicGroup,
  RateConfig,
  RateFactor
} from "../../../../model/rate/RateConfig";
import {LanguagePipe} from "../../../pipe/language.pipe";
import {ConfirmDialogComponent} from "../../confirm-dialog/confirm-dialog.component";
import {ValidationResult} from "../../../../model/rate/RateConfigValidator";
import {EMPTY_RESULT, exactErrorAt, subtreeIssues} from "../validation-paths";

/**
 * One factor within a {@link KindSectionComponent}.
 *
 * Renders:
 *  - a header with `kind` (read-only badge), the auto-derived id (small
 *    diagnostic), and a delete button;
 *  - `property` selector when `kind === 'PER_NUMERIC_PROPERTY'`;
 *  - `signatureRoundingMultiple` and `perItemMinimum` numeric inputs;
 *  - the list of {@link PropertyCharacteristicGroup}s, each rendered
 *    by {@link GroupBuilderComponent};
 *  - "Add group" button;
 *  - `baseAmount` input shown only when `groups.length === 0`.
 *
 * The user does NOT see the factor's `id` or any user-visible label —
 * per the human's direction, the combination of values that make the
 * factor unique is sufficient identification.
 */
@Component({
  selector: 'app-rate-factor-editor',
  templateUrl: './factor-editor.component.html',
  styleUrls: ['./factor-editor.component.css']
})
export class FactorEditorComponent {
  @Input() factor: RateFactor;
  @Input() scopeConfig: Partial<RateConfig> = {};
  @Input() readOnly: boolean = false;
  /** Validator path prefix for THIS factor (e.g. `/SHORT_RUN/factors/2`). */
  @Input() factorPath: string = '';
  @Input() validation: ValidationResult = EMPTY_RESULT;

  @Output() factorChange = new EventEmitter<RateFactor>();
  @Output() factorDelete = new EventEmitter<void>();

  readonly numericProperties: ItemNumericProperty[] = NUMERIC_PROPERTIES;
  readonly localizeNumber = (n: any) => LanguagePipe.localizeNumber(n);
  readonly delocalizeNumber = (s: string) => LanguagePipe.delocalizeNumber(s);

  constructor(private dialog: MatDialog) {}

  /** Validator path of the i-th group within this factor. */
  groupPath(i: number): string {
    return this.factorPath ? `${this.factorPath}/groups/${i}` : '';
  }

  /** Total error count for this factor (used for the header marker). */
  get factorErrorCount(): number {
    return subtreeIssues(this.validation, this.factorPath).errors.length;
  }

  /** True when an error is attached to a specific factor-level field. */
  fieldHasError(suffix: string): boolean {
    return exactErrorAt(this.validation, `${this.factorPath}${suffix}`);
  }

  // ── Primitive setters ────────────────────────────────────────────

  setProperty(prop: ItemNumericProperty): void {
    if (this.readOnly) { return; }
    this.emit({...this.factor, property: prop});
  }

  setBaseAmountStr(raw: string): void {
    if (this.readOnly) { return; }
    if (raw == null || raw === '') {
      const next = {...this.factor};
      delete next.baseAmount;
      this.emit(next);
      return;
    }
    const n = this.delocalizeNumber(raw);
    if (isFinite(n)) { this.emit({...this.factor, baseAmount: n}); }
  }

  setPerItemMinimumStr(raw: string): void {
    if (this.readOnly) { return; }
    if (raw == null || raw === '') {
      const next = {...this.factor};
      delete next.perItemMinimum;
      this.emit(next);
      return;
    }
    const n = this.delocalizeNumber(raw);
    if (isFinite(n)) { this.emit({...this.factor, perItemMinimum: n}); }
  }

  setSignatureRoundingStr(raw: string): void {
    if (this.readOnly) { return; }
    if (raw == null || raw === '') {
      const next = {...this.factor};
      delete next.signatureRoundingMultiple;
      this.emit(next);
      return;
    }
    const n = this.delocalizeNumber(raw);
    if (isFinite(n) && n > 0) { this.emit({...this.factor, signatureRoundingMultiple: n}); }
  }

  // ── Groups ───────────────────────────────────────────────────────

  addGroup(): void {
    if (this.readOnly) { return; }
    // New empty group: no characteristics yet, no cells. The user picks
    // characteristics first; cells are auto-populated once at least one
    // characteristic exists.
    const seed: PropertyCharacteristicGroup = {characteristics: [], cells: {}};
    const next: RateFactor = {
      ...this.factor,
      groups: [...(this.factor.groups || []), seed]
    };
    this.emit(next);
  }

  onGroupChange(index: number, updated: PropertyCharacteristicGroup): void {
    if (this.readOnly) { return; }
    const groups = (this.factor.groups || []).slice();
    if (index >= 0 && index < groups.length) { groups[index] = updated; }
    this.emit({...this.factor, groups});
  }

  onGroupDelete(index: number): void {
    if (this.readOnly) { return; }
    const groups = (this.factor.groups || []).slice();
    if (index >= 0 && index < groups.length) { groups.splice(index, 1); }
    this.emit({...this.factor, groups});
  }

  confirmDelete(): void {
    if (this.readOnly) { return; }
    const ref = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        title: LanguagePipe.get('Delete factor', 'compressSpaces') + LanguagePipe.get('?', 'compressSpaces'),
        body: LanguagePipe.get('This cannot be undone.', 'compressSpaces'),
        noButton: LanguagePipe.get('Cancel', 'compressSpaces'),
        yesButton: LanguagePipe.get('Delete', 'compressSpaces'),
        returnValue: 'ok'
      }
    });
    ref.afterClosed().subscribe(result => {
      if (result === 'ok') { this.factorDelete.emit(); }
    });
  }

  private emit(next: RateFactor): void {
    // The parent ScopePanelComponent re-stamps the id via
    // computeFactorId(...) before storing, so we don't bother here.
    this.factorChange.emit(next);
  }
}

